<template>
  <v-card flat v-if="editItem">
    <v-card-title>
      {{ editItem.name || 'Add New Menu Item' }}
      <v-spacer/>
      <v-btn
        v-if="mealDesignHref"
        outlined
        small
        :href="mealDesignHref"
        target="_blank"
      >
        Open Meal
        <v-icon right small>mdi-open-in-new</v-icon>
      </v-btn>
    </v-card-title>

    <v-tabs v-model="activeTab" grow>
      <v-tab>General</v-tab>
      <v-tab>Ingredients &amp; Combos</v-tab>
      <v-tab>Packing</v-tab>
    </v-tabs>

    <v-card-text class="pt-4">
      <v-form>
        <v-tabs-items v-model="activeTab" class="pt-2">
          <!-- ===== Tab 1: General ===== -->
          <v-tab-item>
            <v-row v-if="!item || !item.id" class="mt-2">
              <v-col cols="12">
                <MealAutocomplete
                  :meal="editMeal"
                  v-on:update:meal="editMeal=$event"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="3">
                <v-text-field
                  label="Meal ID"
                  v-model="editItem.id"
                  disabled
                  dense
                />
              </v-col>
              <v-col cols="7">
                <v-text-field
                  label="Name"
                  :value="editItem.name"
                  disabled
                  dense
                  hint="Fetched from the meal record"
                  persistent-hint
                />
              </v-col>
              <v-col cols="2">
                <v-text-field
                  type="number"
                  label="Price (cents)"
                  v-model="editItem.price"
                  dense
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="4">
                <v-combobox
                  label="Meal Type"
                  v-model="editItem.category"
                  :items="categories"
                  @input="checkIfNew(editItem.category)"
                  dense
                />
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model="editItem.index"
                  label="Index"
                  hint="Sort order"
                  persistent-hint
                  dense
                />
              </v-col>
              <v-col cols="6">
                <v-combobox
                  v-model="editItem.tags"
                  label="Tags"
                  multiple
                  chips
                  deletable-chips
                  small-chips
                  :items="tags"
                  @input="addTags"
                  dense
                  hint="Hit enter after each tag"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  label="Available From (first delivery date)"
                  v-model="editItem.availableFrom"
                  @click="openDatePicker('availableFrom')"
                  hint="Blank = no start. Delivery day: a Sunday or a Wednesday (Sunday covers that week's Monday office deliveries)"
                  persistent-hint
                  clearable
                  dense
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  label="Available To (last delivery date)"
                  v-model="editItem.availableTo"
                  @click="openDatePicker('availableTo')"
                  hint="Blank = no end. Delivery day: a Sunday or a Wednesday (Sunday covers that week's Monday office deliveries)"
                  persistent-hint
                  clearable
                  dense
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-combobox
                  label="Allowed Regions (blank = all regions)"
                  v-model="editItem.regions"
                  multiple
                  chips
                  deletable-chips
                  small-chips
                  return-object
                  :item-text="formatRegion"
                  :items="availableRegions"
                  dense
                />
              </v-col>
            </v-row>

            <v-dialog v-model="showDatePicker" width="300px">
              <!-- delivery days only: Sunday or Wednesday. A Sunday also admits
                   that week's Monday office deliveries (same production), so a
                   Monday is never picked here and the window reads unambiguously -->
              <v-date-picker
                v-model="datePickerValue"
                :allowed-dates="isDeliveryDay"
              />
              <v-toolbar>
                <v-btn @click="showDatePicker=false">Cancel</v-btn>
                <v-spacer/>
                <v-btn @click="saveAndCloseDatePicker">Ok</v-btn>
              </v-toolbar>
            </v-dialog>
          </v-tab-item>

          <!-- ===== Tab 2: Ingredients & Combos ===== -->
          <v-tab-item>
            <v-row class="mt-2">
              <v-col cols="12">
                <v-textarea
                  label="Ingredients Override"
                  v-model="editItem.ingredients"
                  dense
                  rows="4"
                  hint="Any text you enter will be used instead of determining the ingredients from the meal. Allowed input can contain simple html like <strong> or a list <ul><li></ul>."
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-divider class="my-4"/>

            <div class="text-subtitle-2 mb-2">Combo Meals</div>
            <div class="caption mb-2" style="color:#666;">
              In production, this meal will be replaced by these meals.
            </div>
            <div class="d-flex align-center">
              <v-text-field
                label="Qty"
                v-model="subQty"
                type="number"
                min="1"
                step="1"
                style="max-width: 90px"
                class="pr-2"
                dense
              />
              <MealAutocomplete
                :meal="subMeal"
                v-on:update:meal="subMeal=$event"
              />
              <v-btn class="ml-2" @click="addSubMeal(subMeal)" :disabled="!subMeal">Add</v-btn>
            </div>
            <div>
              <ul>
                <li v-for="(sub,i) of editItem.subMeals||[]" v-bind:key="sub.id">
                  {{ sub.quantity }} of {{ sub.id }} {{ sub.name }}
                  <v-btn icon @click="removeSubMeal(i)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </li>
              </ul>
            </div>
          </v-tab-item>

          <!-- ===== Tab 3: Packing ===== -->
          <v-tab-item>
            <v-row class="mt-2">
              <v-col cols="4">
                <v-text-field
                  v-model="editItem.portions"
                  type="number"
                  min="1"
                  label="Portions"
                  dense
                  hint="Used for nutrition calculations"
                  persistent-hint
                />
              </v-col>
              <v-col cols="4">
                <v-text-field
                  :disabled="editItem.noPack"
                  v-model="editItem.containerSize"
                  type="number"
                  min="0"
                  label="Container Size"
                  dense
                  hint="Used for packing in fed boxes"
                  persistent-hint
                />
                <v-checkbox
                  label="Do Not Pack in Box"
                  v-model="editItem.noPack"
                />
              </v-col>
              <v-col cols="4">
                <v-select
                  label="Print Option"
                  v-model="editItem.printOption"
                  :items="['sleeve', 'sticker', 'none']"
                  dense
                  hint="PDF label print style"
                  persistent-hint
                />
              </v-col>
            </v-row>

            <v-alert type="info" text dense class="mt-2 mb-4">
              <div style="font-size: 12px;">
                <strong>Portions:</strong> Determines nutrition information scaling<br/>
                <strong>Container Size:</strong>
                Space taken in fed box (1 = standard meal, 0 = does not take up space,
                blank = do not pack in box)
                <br/>
                <strong>Print:</strong> Label format (sleeve = wrap-around, sticker = 3x5 label, none = does not show up in
                label printing screens)
              </div>
            </v-alert>
          </v-tab-item>
        </v-tabs-items>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn outlined color="red" @click="confirmDelete">Delete</v-btn>
      <v-spacer/>
      <v-btn outlined @click="$emit('close')">Cancel</v-btn>
      <v-spacer/>
      <v-btn outlined @click="handleSave">Save</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import MealAutocomplete from "@/components/MealAutocomplete";
import {deepCopy} from "@/store/utils";
import api from "@/api";
import moment from 'moment';
import {getNextOrderingDate} from '../store/utils';

export default {
  name: "MenuEditDialog",
  components: {MealAutocomplete},
  props: {
    item: {type: Object, default: () => ({}), required: false},
    tags: {},
    categories: {type: Array, default: null, required: false}
  },
  data() {
    return {
      activeTab: 0,
      subMeal: null,
      subQty: 1,
      editMeal: null,
      editItem: null,
      editTags: [],
      datePickerValue: null,
      showDatePicker: null,
      datePickerProp: null,
      availableRegions: [],
    }
  },
  computed: {
    mealDesignHref() {
      const id = this.editItem && Number.parseInt(this.editItem.id);
      if (!Number.isFinite(id)) return null;
      return this.$router.resolve({name: 'MealDesign', params: {id}}).href;
    }
  },
  watch: {
    editMeal(m) {
      console.log('editMeal', m);
      if (m) {
        this.editItem.name = m.name;
        this.editItem.id = m.id;
        this.editItem.noPack = !m.containerSize && !m.containerSize !== 0;
      }
    },
    item: 'fetchData'
  },
  async mounted() {
    this.editTags = deepCopy(this.tags);
    await this.fetchRegions();
    return this.fetchData();
  },
  methods: {
    moment,
    addSubMeal(meal) {
      console.log('adding sub meal', meal);
      const {id, name} = meal;
      const subMeals = this.editItem.subMeals || [];
      subMeals.push({id, name, quantity: this.subQty});
      this.$set(this.editItem, 'subMeals', subMeals);
      this.subQty = 1;
      this.subMeal = null;
    },
    removeSubMeal(index) {
      const subMeals = this.editItem.subMeals || [];
      subMeals.splice(index, 1);
      this.$set(this.editItem, 'subMeals', subMeals);
    },
    async fetchRegions() {
      try {
        const {data} = await api.get("v2/postcodes");
        if (data.postcodes && data.postcodes.regions) {
          this.availableRegions = Object.keys(data.postcodes.regions).map(regionId => {
            const parts = regionId.split(":");
            return {
              id: regionId,
              area: parts[0] || "",
              city: parts[1] || "",
              province: parts[2] || ""
            };
          });
        }
      } catch (error) {
        console.error("Error fetching regions:", error);
      }
    },
    fetchData() {
      const item = this.item;
      this.editItem = deepCopy(item);
      this.editItem.noPack = !item.containerSize && item.containerSize !== 0;
      this.activeTab = 0;
      if (item && item.id) {
        this.fetchMeal(item.id);
      }
    },
    fetchMeal(id) {
      id = Number.parseInt(id);
      if (!Number.isFinite(id)) return;
      return api
        .get(`v2/meal/${id}`)
        .then(({data}) => {
          if (data && data.name) {
            this.$set(this.editItem, 'name', data.name);
          }
        })
        .catch(e => console.error('failed to fetch meal for name', id, e));
    },
    checkIfNew(category) {
      if (category && !this.categories.includes(category)) {
        if (confirm(`Would you like to create a new category called "${category}"?`)) {
          this.categories.push(category);
          this.$emit('add-category', category);
        } else {
          this.editItem.category = '';
        }
      }
    },
    addTags(tags) {
      tags.forEach(tag => {
        if (!this.tags.includes(tag)) {
          this.$emit('add-tag', tag);
        }
      });
    },
    isDeliveryDay(date) {
      const day = moment(date).day();
      return day === 0 || day === 3;
    },
    openDatePicker(prop) {
      this.datePickerProp = prop;
      this.datePickerValue = this.editItem[prop] || null;
      this.showDatePicker = true;
    },
    saveAndCloseDatePicker() {
      const prop = this.datePickerProp;
      if (prop === 'availableTo') {
        if (!moment(this.datePickerValue).isAfter(getNextOrderingDate())) {
          if (!confirm(`Available To date (${this.datePickerValue}) must be after the next ordering date ${getNextOrderingDate()}.  ` +
            `If you decide to ignore this, you need to use OrderEdit page to remove the meal from any customer who ordered it`))
            return;
        }
      }
      this.editItem[prop] = `${this.datePickerValue}`;
      this.showDatePicker = false;
    },
    confirmDelete() {
      if (confirm('Are you sure?\n\nDelete can cause problems if anyone has ordered this meal.  It is better to mark it unavailable by setting Available To')) {
        this.$emit('delete', this.editItem);
        this.$emit('close')
      }
    },
    formatRegion(region) {
      if (!region) return '';
      if (typeof region === 'string') {
        const parts = region.split(':');
        return `${parts[0]}, ${parts[1]}, ${parts[2]}`;
      }
      return `${region.area}, ${region.city}, ${region.province}`;
    },
    handleSave() {
      this.$emit('save', this.editItem);
      this.$emit('close');
    }
  },

}
</script>

<style scoped>

</style>
